import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { Link } from "react-router-dom";

export const AdminManageStaticWarmupRoutine = () => {
  const { staff } = useStaffAuthContext();
  const [warmups, setWarmups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchWarmups = async () => {
      try {
        setIsLoading(true);
        setError("");
  
        const response = await fetch(`${API_URL.GET_STATIC_WARMUP_ROUTINES}?page=${currentPage}&limit=${itemsPerPage}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch warmup routines.");
        }

        const data = await response.json();
        setWarmups(data.warmupRoutines || []);
        setTotalPages(data.totalPages || 1); // Set total pages from response
      } catch (err) {
        setError(err.message || "Error fetching warmup routines.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarmups();
  }, [staff.token, currentPage, itemsPerPage]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  const filteredWarmups = warmups.filter((warmup) =>
    warmup.title.toLowerCase().includes(searchQuery)
  );

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this warmup routine?")) {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL.DELETE_STATIC_WARMUP_ROUTINE}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete warmup routine.");
        }
        setWarmups((prev) => prev.filter((warmup) => warmup._id !== id));
      } catch (err) {
        setError(err.message || "Error deleting warmup routine.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        <div className="flex justify-between mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search warmups..."
            className="p-2 border rounded w-1/2"
          />
          <Link
            to="/administrator/warmup-routine/static/create"
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white font-semibold rounded-md shadow hover:bg-blue-600"
          >
            Create Static Warmup Template
          </Link>
        </div>
        <h1 className="text-2xl font-bold mb-4">Manage Static Warmup Routine Templates</h1>
        {error && <div className="bg-red-100 text-red-500 p-3 rounded mb-4">{error}</div>}
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="flex-1 overflow-auto border rounded shadow bg-white">
            <table className="table-auto w-full">
              <thead className="sticky top-0 bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarmups.map((warmup, index) => (
                  <tr
                    key={warmup._id}
                    className={`hover:bg-gray-100 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className="px-4 py-2">{warmup.title}</td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/administrator/warmup-routine/static/edit/${warmup._id}`}
                        className="inline-block px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 mr-4"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(warmup._id)}
                        className="inline-block px-4 py-2 bg-red-500 text-white rounded-md shadow hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center mt-4 p-4">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
